import { useState, useEffect } from 'react';
import {
  Users, Server, Database, Square, RefreshCw, Trash, Shield, Ban,
  UserCog, ExternalLink, ChevronRight, Search, Filter, Download,
  AlertCircle, CheckCircle, XCircle, Clock, HardDrive, Cpu, MemoryStick,
  Activity, Zap, Settings
} from 'lucide-react';
import * as Collapsible from '@radix-ui/react-collapsible';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';

interface UserResource {
  userId: string;
  email: string;
  githubUsername: string | null;
  isSuperUser: boolean;
  isActive: boolean;
  totalDiskMB: number;
  totalRamMB: number;
  totalCpuPercent: number;
  containerRamMB: number;
  containerCpuPercent: number;
  projectCount: number;
  runningServers: number;
  lastActivity: Date | null;
  createdAt: Date;
}

interface ProjectResource {
  projectId: string;
  projectName: string;
  path: string;
  diskMB: number;
  ramMB: number;
  cpuPercent: number;
  framework: string | null;
  devServerStatus: 'running' | 'stopped' | 'error';
  devServerUrl: string | null;
  devServerPort: number | null;
  containerRunning: boolean;
  lastAccessed: Date | null;
}

interface SystemStats {
  system: {
    totalRamMB: number;
    usedRamMB: number;
    freeRamMB: number;
    totalDiskGB: number;
    usedDiskGB: number;
    freeDiskGB: number;
  };
  users: {
    total: number;
    activeContainers: number;
  };
}

export function AdminProcesses() {
  const [users, setUsers] = useState<UserResource[]>([]);
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [userProjects, setUserProjects] = useState<Map<string, ProjectResource[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'cpu' | 'ram' | 'disk' | 'username' | 'lastActivity'>('username');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'suspended' | 'superusers'>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => void;
  } | null>(null);

  // Fetch all users and system stats
  const fetchData = async () => {
    try {
      const [usersResponse, statsResponse] = await Promise.all([
        api.get('/admin/processes'),
        api.get('/admin/processes/system/stats')
      ]);

      setUsers((usersResponse as any).users);
      setSystemStats(statsResponse as any);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch projects for a user
  const fetchUserProjects = async (userId: string) => {
    try {
      const response = await api.get(`/admin/processes/users/${userId}/projects`);
      setUserProjects(prev => new Map(prev).set(userId, (response as any).projects));
    } catch (error) {
      console.error('Error fetching user projects:', error);
    }
  };

  // Toggle user expansion
  const toggleUser = (userId: string) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
      // Fetch projects when expanding
      if (!userProjects.has(userId)) {
        fetchUserProjects(userId);
      }
    }
    setExpandedUsers(newExpanded);
  };

  // Toggle project expansion
  const toggleProject = (projectId: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  // User actions
  const handleSuspendUser = async (userId: string, email: string) => {
    setConfirmDialog({
      open: true,
      title: 'Suspend User',
      message: `Are you sure you want to suspend ${email}? This will stop all their processes and log them out immediately.`,
      action: async () => {
        try {
          await api.post(`/admin/processes/users/${userId}/suspend`);
          await fetchData();
        } catch (error) {
          console.error('Error suspending user:', error);
        }
      }
    });
  };

  const handleKillAllProcesses = async (userId: string, email: string) => {
    setConfirmDialog({
      open: true,
      title: 'Kill All Processes',
      message: `Are you sure you want to kill ALL processes for ${email}? This is an emergency stop that will immediately terminate their container and all dev servers.`,
      action: async () => {
        try {
          await api.post(`/admin/processes/users/${userId}/kill-all`);
          await fetchData();
        } catch (error) {
          console.error('Error killing processes:', error);
        }
      }
    });
  };

  const handleDeleteUser = async (userId: string, email: string) => {
    setConfirmDialog({
      open: true,
      title: 'Delete User - PERMANENT',
      message: `⚠️ WARNING: Are you ABSOLUTELY SURE you want to delete ${email}? This will permanently delete:\n\n• Their account\n• All projects and code\n• All databases\n• All settings\n\nThis action CANNOT be undone!`,
      action: async () => {
        try {
          await api.delete(`/admin/processes/users/${userId}`);
          await fetchData();
        } catch (error) {
          console.error('Error deleting user:', error);
        }
      }
    });
  };

  const handleToggleSuperUser = async (userId: string, currentStatus: boolean) => {
    try {
      await api.post(`/admin/processes/users/${userId}/permissions`, {
        isSuperUser: !currentStatus
      });
      await fetchData();
    } catch (error) {
      console.error('Error updating permissions:', error);
    }
  };

  const handleImpersonate = async (userId: string) => {
    try {
      const response = await api.post(`/admin/processes/users/${userId}/impersonate`);
      window.open(`${response.data.impersonateUrl}`, '_blank');
    } catch (error) {
      console.error('Error generating impersonation token:', error);
    }
  };

  const handleRestartContainer = async (userId: string) => {
    try {
      await api.post(`/admin/processes/users/${userId}/container/restart`);
      await fetchData();
    } catch (error) {
      console.error('Error restarting container:', error);
    }
  };

  // Project actions
  const handleStopServer = async (port: number) => {
    try {
      await api.post(`/admin/processes/servers/${port}/stop`);
      await fetchData();
    } catch (error) {
      console.error('Error stopping server:', error);
    }
  };

  const handleWipeServer = async (projectId: string, projectName: string) => {
    setConfirmDialog({
      open: true,
      title: 'Wipe Dev Server',
      message: `Are you sure you want to wipe the dev server for "${projectName}"? This will clear build artifacts and caches but keep the source code.`,
      action: async () => {
        try {
          await api.post(`/admin/processes/projects/${projectId}/wipe`);
          await fetchData();
        } catch (error) {
          console.error('Error wiping server:', error);
        }
      }
    });
  };

  // Export data
  const handleExport = () => {
    const csv = [
      ['Email', 'GitHub', 'SuperUser', 'CPU%', 'RAM(MB)', 'Disk(MB)', 'Projects', 'Servers', 'LastActivity'].join(','),
      ...filteredAndSortedUsers.map(u => [
        u.email,
        u.githubUsername || '',
        u.isSuperUser ? 'Yes' : 'No',
        u.totalCpuPercent,
        u.totalRamMB,
        u.totalDiskMB,
        u.projectCount,
        u.runningServers,
        u.lastActivity ? new Date(u.lastActivity).toISOString() : ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uncommonable-processes-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Filter and sort users
  const filteredAndSortedUsers = users
    .filter(user => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery ||
        user.email.toLowerCase().includes(searchLower) ||
        user.githubUsername?.toLowerCase().includes(searchLower);

      // Status filter
      let matchesStatus = true;
      if (filterStatus === 'active') matchesStatus = user.isActive;
      if (filterStatus === 'suspended') matchesStatus = !user.isActive;
      if (filterStatus === 'superusers') matchesStatus = user.isSuperUser;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'cpu': return b.totalCpuPercent - a.totalCpuPercent;
        case 'ram': return b.totalRamMB - a.totalRamMB;
        case 'disk': return b.totalDiskMB - a.totalDiskMB;
        case 'lastActivity':
          return (b.lastActivity?.getTime() || 0) - (a.lastActivity?.getTime() || 0);
        case 'username':
        default:
          return (a.githubUsername || a.email).localeCompare(b.githubUsername || b.email);
      }
    });

  // Auto-refresh every 30s
  useEffect(() => {
    fetchData();
    if (autoRefresh) {
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  // Helper functions
  const formatBytes = (mb: number) => {
    if (mb < 1024) return `${mb} MB`;
    return `${(mb / 1024).toFixed(1)} GB`;
  };

  const formatTimeAgo = (date: Date | null) => {
    if (!date) return 'Never';
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const getResourceColor = (percent: number) => {
    if (percent >= 95) return 'text-red-600';
    if (percent >= 80) return 'text-orange-500';
    if (percent >= 50) return 'text-yellow-500';
    return 'text-green-600';
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { color: string; icon: JSX.Element }> = {
      running: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3 h-3" /> },
      stopped: { color: 'bg-gray-100 text-gray-800', icon: <XCircle className="w-3 h-3" /> },
      error: { color: 'bg-red-100 text-red-800', icon: <AlertCircle className="w-3 h-3" /> }
    };
    const variant = variants[status] || variants.stopped;
    return (
      <Badge className={`${variant.color} flex items-center gap-1`}>
        {variant.icon}
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* System Stats Header */}
      {systemStats && (
        <div className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold">System Processes</h1>
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                <Users className="w-4 h-4 mr-1" />
                {systemStats.users.total} users
              </Badge>
              <Badge variant="outline">
                <Server className="w-4 h-4 mr-1" />
                {systemStats.users.activeContainers} containers
              </Badge>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* RAM Usage */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <MemoryStick className="w-4 h-4" />
                  RAM Usage
                </span>
                <span className="font-mono">
                  {formatBytes(systemStats.system.usedRamMB)} / {formatBytes(systemStats.system.totalRamMB)}
                </span>
              </div>
              <Progress
                value={(systemStats.system.usedRamMB / systemStats.system.totalRamMB) * 100}
                className="h-2"
              />
            </div>

            {/* Disk Usage */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <HardDrive className="w-4 h-4" />
                  Disk Usage
                </span>
                <span className="font-mono">
                  {systemStats.system.usedDiskGB} GB / {systemStats.system.totalDiskGB} GB
                </span>
              </div>
              <Progress
                value={(systemStats.system.usedDiskGB / systemStats.system.totalDiskGB) * 100}
                className="h-2"
              />
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white border-b px-6 py-3 flex items-center gap-4">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search users, emails, projects..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filter */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button variant="outline" size="sm">
              <Filter className="w-4 h-4 mr-2" />
              Filter: {filterStatus}
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            <DropdownMenu.Item onClick={() => setFilterStatus('all')}>
              All Users
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => setFilterStatus('active')}>
              Active Only
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => setFilterStatus('suspended')}>
              Suspended Only
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => setFilterStatus('superusers')}>
              Super Users
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>

        {/* Sort */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button variant="outline" size="sm">
              <Activity className="w-4 h-4 mr-2" />
              Sort
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            <DropdownMenu.Item onClick={() => setSortBy('username')}>
              Username
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => setSortBy('cpu')}>
              CPU Usage
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => setSortBy('ram')}>
              RAM Usage
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => setSortBy('disk')}>
              Disk Usage
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => setSortBy('lastActivity')}>
              Last Activity
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>

        {/* Export */}
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>

        {/* Auto-refresh */}
        <Button
          variant={autoRefresh ? "default" : "outline"}
          size="sm"
          onClick={() => setAutoRefresh(!autoRefresh)}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
          Auto-refresh
        </Button>

        {/* Manual refresh */}
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Users List */}
      <div className="flex-1 overflow-auto px-6 py-4 space-y-2">
        {filteredAndSortedUsers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p>No users found</p>
          </div>
        ) : (
          filteredAndSortedUsers.map(user => (
            <UserRow
              key={user.userId}
              user={user}
              expanded={expandedUsers.has(user.userId)}
              onToggle={() => toggleUser(user.userId)}
              projects={userProjects.get(user.userId) || []}
              expandedProjects={expandedProjects}
              onToggleProject={toggleProject}
              onSuspend={() => handleSuspendUser(user.userId, user.email)}
              onKillAll={() => handleKillAllProcesses(user.userId, user.email)}
              onDelete={() => handleDeleteUser(user.userId, user.email)}
              onToggleSuperUser={() => handleToggleSuperUser(user.userId, user.isSuperUser)}
              onImpersonate={() => handleImpersonate(user.userId)}
              onRestartContainer={() => handleRestartContainer(user.userId)}
              onStopServer={handleStopServer}
              onWipeServer={handleWipeServer}
              formatBytes={formatBytes}
              formatTimeAgo={formatTimeAgo}
              getResourceColor={getResourceColor}
              getStatusBadge={getStatusBadge}
            />
          ))
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog.Root open={confirmDialog?.open} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[500px] shadow-xl">
            <Dialog.Title className="text-xl font-bold mb-2">
              {confirmDialog?.title}
            </Dialog.Title>
            <Dialog.Description className="text-gray-600 mb-6 whitespace-pre-line">
              {confirmDialog?.message}
            </Dialog.Description>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setConfirmDialog(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  confirmDialog?.action();
                  setConfirmDialog(null);
                }}
              >
                Confirm
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

// User Row Component
function UserRow({
  user,
  expanded,
  onToggle,
  projects,
  expandedProjects,
  onToggleProject,
  onSuspend,
  onKillAll,
  onDelete,
  onToggleSuperUser,
  onImpersonate,
  onRestartContainer,
  onStopServer,
  onWipeServer,
  formatBytes,
  formatTimeAgo,
  getResourceColor,
  getStatusBadge
}: any) {
  return (
    <Collapsible.Root open={expanded} onOpenChange={onToggle}>
      <div className="bg-white rounded-lg border hover:shadow-md transition-shadow">
        {/* User Header */}
        <div className="flex items-center gap-4 p-4">
          <Collapsible.Trigger className="flex items-center gap-2 hover:bg-gray-50 px-2 py-1 rounded">
            <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </Collapsible.Trigger>

          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
              {(user.githubUsername || user.email)[0].toUpperCase()}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold truncate">
                  {user.githubUsername || user.email.split('@')[0]}
                </span>
                {user.isSuperUser && (
                  <Badge className="bg-purple-100 text-purple-800">
                    <Shield className="w-3 h-3 mr-1" />
                    Super User
                  </Badge>
                )}
                {!user.isActive && (
                  <Badge className="bg-red-100 text-red-800">
                    <Ban className="w-3 h-3 mr-1" />
                    Suspended
                  </Badge>
                )}
              </div>
              <div className="text-sm text-gray-500">{user.email}</div>
            </div>

            {/* Resource Stats */}
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-gray-400" />
                <span className={`font-mono ${getResourceColor(user.totalCpuPercent)}`}>
                  {user.totalCpuPercent.toFixed(1)}%
                </span>
              </div>
              <div className="flex items-center gap-2">
                <MemoryStick className="w-4 h-4 text-gray-400" />
                <span className="font-mono">{formatBytes(user.totalRamMB)}</span>
              </div>
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-gray-400" />
                <span className="font-mono">{formatBytes(user.totalDiskMB)}</span>
              </div>
              <Badge variant="outline">{user.projectCount} projects</Badge>
              <Badge variant="outline">{user.runningServers} servers</Badge>
              <div className="text-gray-500">
                <Clock className="w-4 h-4 inline mr-1" />
                {formatTimeAgo(user.lastActivity)}
              </div>
            </div>

            {/* Actions Menu */}
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button variant="ghost" size="sm">
                  <Settings className="w-4 h-4" />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end" className="w-56">
                <DropdownMenu.Item onClick={onToggleSuperUser}>
                  <Shield className="w-4 h-4 mr-2" />
                  {user.isSuperUser ? 'Remove' : 'Make'} Super User
                </DropdownMenu.Item>
                <DropdownMenu.Item onClick={onImpersonate}>
                  <UserCog className="w-4 h-4 mr-2" />
                  Log in as User
                </DropdownMenu.Item>
                <DropdownMenu.Item onClick={onRestartContainer}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Restart Container
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
                <DropdownMenu.Item onClick={onSuspend} className="text-orange-600">
                  <Ban className="w-4 h-4 mr-2" />
                  Suspend User
                </DropdownMenu.Item>
                <DropdownMenu.Item onClick={onKillAll} className="text-red-600">
                  <Zap className="w-4 h-4 mr-2" />
                  Kill All Processes
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
                <DropdownMenu.Item onClick={onDelete} className="text-red-600">
                  <Trash className="w-4 h-4 mr-2" />
                  Delete User
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </div>
        </div>

        {/* Projects List (Expanded) */}
        <Collapsible.Content>
          <div className="border-t px-4 pb-4 pt-2 bg-gray-50 space-y-2">
            {projects.length === 0 ? (
              <div className="text-center py-4 text-gray-500 text-sm">
                No projects
              </div>
            ) : (
              projects.map((project: ProjectResource) => (
                <ProjectRow
                  key={project.projectId}
                  project={project}
                  expanded={expandedProjects.has(project.projectId)}
                  onToggle={() => onToggleProject(project.projectId)}
                  onStopServer={() => project.devServerPort && onStopServer(project.devServerPort)}
                  onWipeServer={() => onWipeServer(project.projectId, project.projectName)}
                  formatBytes={formatBytes}
                  formatTimeAgo={formatTimeAgo}
                  getResourceColor={getResourceColor}
                  getStatusBadge={getStatusBadge}
                />
              ))
            )}
          </div>
        </Collapsible.Content>
      </div>
    </Collapsible.Root>
  );
}

// Project Row Component
function ProjectRow({
  project,
  expanded,
  onToggle,
  onStopServer,
  onWipeServer,
  formatBytes,
  formatTimeAgo,
  getResourceColor,
  getStatusBadge
}: any) {
  return (
    <Collapsible.Root open={expanded} onOpenChange={onToggle}>
      <div className="bg-white rounded border hover:border-blue-300 transition-colors">
        <div className="flex items-center gap-3 p-3">
          <Collapsible.Trigger>
            <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </Collapsible.Trigger>

          <Database className="w-4 h-4 text-gray-400" />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium truncate">{project.projectName}</span>
              {project.framework && (
                <Badge variant="outline" className="text-xs">
                  {project.framework}
                </Badge>
              )}
              {getStatusBadge(project.devServerStatus)}
            </div>
          </div>

          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Cpu className="w-3 h-3 text-gray-400" />
              <span className={`font-mono text-xs ${getResourceColor(project.cpuPercent)}`}>
                {project.cpuPercent.toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center gap-2">
              <MemoryStick className="w-3 h-3 text-gray-400" />
              <span className="font-mono text-xs">{formatBytes(project.ramMB)}</span>
            </div>
            <div className="flex items-center gap-2">
              <HardDrive className="w-3 h-3 text-gray-400" />
              <span className="font-mono text-xs">{formatBytes(project.diskMB)}</span>
            </div>
            <span className="text-xs text-gray-500">{formatTimeAgo(project.lastAccessed)}</span>
          </div>

          {project.devServerUrl && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(project.devServerUrl, '_blank')}
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          )}

          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button variant="ghost" size="sm">
                <Settings className="w-3 h-3" />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {project.devServerUrl && (
                <DropdownMenu.Item onClick={() => window.open(project.devServerUrl, '_blank')}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Dev Server
                </DropdownMenu.Item>
              )}
              {project.devServerStatus === 'running' && (
                <DropdownMenu.Item onClick={onStopServer}>
                  <Square className="w-4 h-4 mr-2" />
                  Stop Server
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Item onClick={onWipeServer} className="text-orange-600">
                <Trash className="w-4 h-4 mr-2" />
                Wipe Dev Server
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </div>

{/* Dev Server Details (Expanded) */}
        <Collapsible.Content>
          <div className="border-t px-4 py-3 bg-gray-50">
            <div className="text-sm space-y-1">
              {project.devServerUrl ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Dev Server URL:</span>
                    <a
                      href={project.devServerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-mono text-xs"
                    >
                      {project.devServerUrl}
                    </a>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Port:</span>
                    <span className="font-mono text-xs">{project.devServerPort}</span>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Dev Server:</span>
                  <span className="text-xs text-gray-500">Not running</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Framework:</span>
                <span className="text-xs">{project.framework || 'Unknown'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Path:</span>
                <span className="font-mono text-xs text-gray-500 truncate max-w-xs" title={project.path}>
                  {project.path}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Last Accessed:</span>
                <span className="text-xs text-gray-500">
                  {project.lastAccessed ? formatTimeAgo(project.lastAccessed) : 'Never'}
                </span>
              </div>
              {project.containerRunning && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Container:</span>
                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300">
                    Running
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </Collapsible.Content>

      </div>
    </Collapsible.Root>
  );
}
